<template>
  <div class="game-detail">
    <div v-if="isLoading" class="loading">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Loading game details...</p>
    </div>

    <div v-else-if="!game" class="no-game">
      <i class="fas fa-exclamation-circle"></i>
      <p>Game not found</p>
      <button @click="goToHome" class="back-button">Back to Scores</button>
    </div>

    <div v-else class="game-container">
      <div class="game-header">
        <button @click="goToHome" class="back-button">
          <i class="fas fa-arrow-left"></i> Back to Scores
        </button>
        <div class="game-status" :class="getStatusClass(game.status)">
          {{ getStatusText(game) }}
        </div>
      </div>

      <div class="game-card">
        <!-- Game Info Header -->
        <div class="game-info-header">
          <div class="game-meta">
            <div class="game-date-time">
              <i class="fas fa-calendar-alt"></i>
              {{ formattedDate }}
            </div>
            <div v-if="game.venue" class="game-venue">
              <i class="fas fa-map-marker-alt"></i>
              {{ game.venue.name }}, {{ game.venue.city }}
            </div>
            <div v-if="game.tv" class="game-broadcast">
              <i class="fas fa-tv"></i>
              {{ game.tv }}
            </div>
            <div
              v-if="game.weather && game.weather.temperature"
              class="game-weather"
            >
              <i class="fas fa-temperature-high"></i>
              {{ Math.round(game.weather.temperature) }}°F
            </div>
          </div>
          <div class="game-status-large" :class="getStatusClass(game.status)">
            {{ getStatusText(game) }}
          </div>
        </div>

        <!-- Teams with Integrated Scores -->
        <div class="teams-section">
          <div v-if="game.home_team" class="team-row home-team">
            <div class="team-main">
              <div class="team-logo">
                <img
                  v-if="getTeamLogoUrl(game.home_team)"
                  :src="getTeamLogoUrl(game.home_team)"
                  :alt="game.home_team.school || game.home_team.name"
                  @error="handleImageError"
                />
                <div v-else class="logo-placeholder">
                  {{ getTeamInitial(game.home_team) }}
                </div>
              </div>
              <div class="team-info">
                <div class="team-name">
                  {{ game.home_team.school || game.home_team.name }}
                  <span
                    v-if="game.home_team.rank && game.home_team.rank < 99"
                    class="team-rank"
                  >
                    #{{ game.home_team.rank }}
                  </span>
                </div>
                <div class="team-details">
                  <span class="team-record">{{
                    game.home_team.conference || "Independent"
                  }}</span>
                </div>
              </div>
            </div>
            <div class="team-score-section">
              <div v-if="game.status !== 'scheduled'" class="team-score">
                {{ game.home_team.points || 0 }}
              </div>
              <div class="team-location-badge">Home</div>
            </div>
          </div>

          <div v-if="game.away_team" class="team-row away-team">
            <div class="team-main">
              <div class="team-logo">
                <img
                  v-if="getTeamLogoUrl(game.away_team)"
                  :src="getTeamLogoUrl(game.away_team)"
                  :alt="game.away_team.school || game.away_team.name"
                  @error="handleImageError"
                />
                <div v-else class="logo-placeholder">
                  {{ getTeamInitial(game.away_team) }}
                </div>
              </div>
              <div class="team-info">
                <div class="team-name">
                  {{ game.away_team.school || game.away_team.name }}
                  <span
                    v-if="game.away_team.rank && game.away_team.rank < 99"
                    class="team-rank"
                  >
                    #{{ game.away_team.rank }}
                  </span>
                </div>
                <div class="team-details">
                  <span class="team-record">{{
                    game.away_team.conference || "Independent"
                  }}</span>
                </div>
              </div>
            </div>
            <div class="team-score-section">
              <div v-if="game.status !== 'scheduled'" class="team-score">
                {{ game.away_team.points || 0 }}
              </div>
              <div class="team-location-badge">Away</div>
            </div>
          </div>
        </div>

        <!-- Line Scores Section -->
        <div
          v-if="
            game.status !== 'scheduled' &&
            (game.home_team.lineScores || game.away_team.lineScores)
          "
          class="line-scores-section"
        >
          <h3>Scoring by Quarter</h3>
          <div
            class="line-scores-grid"
            :style="{ gridTemplateColumns: getLineScoresGridColumns() }"
          >
            <div class="line-scores-header">
              <div class="team-header"></div>
              <div
                class="period-header"
                v-for="(period, index) in getLineScoresPeriods()"
                :key="index"
              >
                {{ period }}
              </div>
              <div class="total-header">Total</div>
            </div>
            <div class="line-scores-row">
              <div class="team-name-cell">
                {{ getTeamDisplayName(game.away_team) }}
              </div>
              <div
                class="score-cell"
                v-for="(score, index) in game.away_team.lineScores"
                :key="index"
              >
                {{ score || 0 }}
              </div>
              <div class="score-cell total">
                {{ game.away_team.points || 0 }}
              </div>
            </div>
            <div class="line-scores-row">
              <div class="team-name-cell">
                {{ getTeamDisplayName(game.home_team) }}
              </div>
              <div
                class="score-cell"
                v-for="(score, index) in game.home_team.lineScores"
                :key="index"
              >
                {{ score || 0 }}
              </div>
              <div class="score-cell total">
                {{ game.home_team.points || 0 }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { default as dataService } from "../utils/dataService";
import {
  getTeamInitial,
  handleImageError,
  getTeamDisplayName,
  getTeamLogoUrl,
} from "../utils/teamUtils";
import { cookieUtils } from "../utils/cookieUtils";
import { timezoneUtils } from "../utils/timezoneUtils";

export default {
  name: "GameDetailView",
  data() {
    return {
      game: null,
      isLoading: false,
    };
  },
  computed: {
    gameId() {
      return this.$route.params.id;
    },
    userTimezone() {
      return cookieUtils.getUserSelectedTimezone() || timezoneUtils.getUserTimezone();
    },
    use12HourFormat() {
      return cookieUtils.getUse12HourFormat();
    },
    formattedDate() {
      if (!this.game) return "";

      return timezoneUtils.formatDateTime(this.game.start_date, this.userTimezone, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: this.use12HourFormat
      });
    },
  },
  created() {
    this.fetchGameDetails();
  },
  methods: {
    getTeamInitial,
    handleImageError,
    getTeamDisplayName,
    getTeamLogoUrl,
    async fetchGameDetails() {
      this.isLoading = true;
      try {
        // Load comprehensive game data to get team-enhanced data
        const allGames = await dataService.loadGameData(null, false);
        this.game = allGames.find((game) => game.id === parseInt(this.gameId));

        // If not found in comprehensive data, try searching specific dates
        if (!this.game) {
          // Try to find the game by checking multiple dates
          const dates = this.getRecentDates();
          for (const date of dates) {
            const gamesForDate = await dataService.loadGameData(date, false);
            const foundGame = gamesForDate.find(
              (game) => game.id === parseInt(this.gameId)
            );
            if (foundGame) {
              this.game = foundGame;
              break;
            }
          }
        }
      } catch (error) {
        console.error("Error loading game details:", error);
        this.game = null;
      } finally {
        this.isLoading = false;
      }
    },
    getStatusClass(status) {
      if (status === "scheduled") return "status-pre";
      if (status === "in_progress") return "status-in";
      if (status === "completed") return "status-post";
      return "";
    },
    getStatusText(game) {
      if (game.status === "scheduled") {
        return "Scheduled";
      } else if (game.status === "in_progress") {
        return game.clock ? `${game.period}Q - ${game.clock}` : "In Progress";
      } else if (game.status === "completed") {
        return "Final";
      }
      return game.status;
    },
    getRecentDates() {
      const dates = [];
      for (let i = -3; i <= 3; i++) {
        const date = new Date();
        date.setDate(date.getDate() + i);
        dates.push(date.toISOString().split("T")[0]);
      }
      return dates;
    },
    getLineScoresPeriods() {
      // Determine how many periods based on the sport and game data
      const maxPeriods = Math.max(
        this.game?.home_team?.lineScores?.length || 0,
        this.game?.away_team?.lineScores?.length || 0
      );

      if (maxPeriods === 0) return [];

      // College football has 4 quarters, but we'll adapt based on data
      const periods = [];
      for (let i = 1; i <= maxPeriods; i++) {
        periods.push(`Q${i}`);
      }
      return periods;
    },
    getLineScoresGridColumns() {
      const periodCount = Math.max(
        this.game?.home_team?.lineScores?.length || 0,
        this.game?.away_team?.lineScores?.length || 0
      );
      // 2fr for team names + periodCount columns + 1fr for total
      return `2fr repeat(${periodCount}, 60px) 1fr`;
    },
    goToHome() {
      this.$router.push("/");
    },
  },
};
</script>

<style scoped>
.game-detail {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.loading,
.no-game {
  text-align: center;
  padding: 3rem;
  background-color: #161b22;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  border: 1px solid #30363d;
}

.loading i,
.no-game i {
  font-size: 3rem;
  color: #ff7b45;
  margin-bottom: 1rem;
}

.no-game .back-button {
  margin-top: 1rem;
}

.game-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.back-button {
  background-color: #238636;
  color: white;
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: 1px solid #2ea043;
}

.back-button i {
  margin-right: 0.5rem;
}

.game-status {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 500;
}

.status-pre {
  background-color: #1e3a5f;
  color: #58a6ff;
}

.status-in {
  background-color: #4a3806;
  color: #ffd43b;
}

.status-post {
  background-color: #1e4620;
  color: #56d364;
}

.game-card {
  background-color: #161b22;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  border: 1px solid #30363d;
}

/* Game Info Header */
.game-info-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem 1.5rem;
  background-color: #0d1117;
  border-bottom: 1px solid #30363d;
}

.game-meta {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.game-meta > div {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #8b949e;
}

.game-venue,
.game-broadcast {
  color: #e0e0e0;
  font-weight: 500;
}

.game-meta i {
  margin-right: 0.5rem;
  width: 14px;
}

.game-status-large {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.875rem;
  white-space: nowrap;
}

/* Teams Section */
.teams-section {
  padding: 1rem;
}

.team-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border-radius: 6px;
  background-color: #0d1117;
  border: 1px solid #30363d;
}

.team-row:last-child {
  margin-bottom: 0;
}

.team-main {
  display: flex;
  align-items: center;
  flex: 1;
}

.team-logo {
  width: 48px;
  height: 48px;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.team-logo img {
  max-width: 100%;
  max-height: 100%;
}

.logo-placeholder {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #4a5568, #2d3748);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
  color: #e2e8f0;
  text-transform: uppercase;
}

.team-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.team-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #e0e0e0;
}

.team-details {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
}

.team-record {
  color: #8b949e;
}

.team-rank {
  background-color: transparent;
  color: #8b949e;
  font-size: 0.7rem;
  font-weight: 400;
  padding: 0;
  border-radius: 0;
  margin-left: 0.5rem;
  opacity: 0.8;
}

.team-score-section {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  margin-left: 1rem;
}

.team-score {
  font-size: 2rem;
  font-weight: 700;
  color: #e0e0e0;
  min-width: 60px;
  text-align: center;
}

.team-location-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
}

.home-team .team-location-badge {
  background-color: #1e4620;
  color: #56d364;
}

.away-team .team-location-badge {
  background-color: #1e3a5f;
  color: #58a6ff;
}

/* Line Scores Section */
.line-scores-section {
  padding: 1.5rem;
  border-top: 1px solid #30363d;
  background-color: #0d1117;
}

.line-scores-section h3 {
  margin: 0 0 1rem 0;
  color: #c9d1d9;
  font-size: 1.125rem;
  font-weight: 600;
}

.line-scores-grid {
  display: grid;
  gap: 0.5rem;
  align-items: center;
}

.line-scores-header {
  display: contents;
}

.team-header {
  padding: 0.75rem 0.5rem;
  font-weight: 600;
  color: #8b949e;
  text-align: left;
}

.period-header,
.total-header {
  padding: 0.75rem 0.5rem;
  font-weight: 600;
  color: #8b949e;
  text-align: center;
  background-color: #161b22;
  border-radius: 4px;
  border: 1px solid #30363d;
}

.total-header {
  background-color: #238636;
  color: white;
  font-weight: 700;
}

.line-scores-row {
  display: contents;
}

.team-name-cell {
  padding: 0.75rem 0.5rem;
  font-weight: 500;
  color: #e0e0e0;
  background-color: #161b22;
  border-radius: 4px;
  border: 1px solid #30363d;
}

.score-cell {
  padding: 0.75rem 0.5rem;
  text-align: center;
  font-weight: 600;
  color: #e0e0e0;
  background-color: #161b22;
  border-radius: 4px;
  border: 1px solid #30363d;
  min-width: 50px;
}

.score-cell.total {
  background-color: #21262d;
  color: #ffd43b;
  font-size: 1.125rem;
  font-weight: 700;
}

@media (max-width: 768px) {
  .game-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .game-status {
    margin-top: 1rem;
  }

  .game-info {
    flex-direction: column;
  }

  .game-info > div {
    margin-right: 0;
    margin-bottom: 0.75rem;
  }

  .team-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .team-logo {
    margin-bottom: 1rem;
  }

  .line-scores-grid {
    gap: 0.25rem;
  }

  .line-scores-section h3 {
    font-size: 1rem;
  }

  .team-name-cell,
  .score-cell,
  .period-header,
  .total-header {
    padding: 0.5rem 0.25rem;
    font-size: 0.875rem;
  }

  .score-cell {
    min-width: 40px;
  }

  .score-cell.total {
    font-size: 1rem;
  }
}
</style>
